import { computed, ref, watch } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import { defineStore } from 'pinia';
import { DateTime } from 'luxon';
import { SubscriptionPlan } from '@getaccept/lib-shared/src/enums/subscription-plan';
import { Feature } from '@getaccept/feature-restriction';
import type { Subscription } from '../types/subscription';
import { SubscriptionStatus } from '../types/subscription-status';
import { SubscriptionHelper } from '../helpers/subscription.helper';
import { SeatType } from '../types/seat-type';
import { MathHelper } from '../../helpers/math.helper';

export const useSubscriptionStore = defineStore('subscription', () => {
  const subscription: Ref<Subscription> = ref(null);
  const isLoaded = ref(false);
  const accessChanged = ref(false);
  const isError = ref(false);

  const setSubscription = (newSubscription: Subscription) => {
    subscription.value = newSubscription;
  };

  const setIsLoaded = (loaded: boolean) => {
    isLoaded.value = loaded;
  };

  const setIsError = (error: boolean) => {
    isError.value = error;
  };

  const setAccessChanged = (value: boolean) => {
    accessChanged.value = value;
  };

  const isTrialPlan = computed(() =>
    [SubscriptionPlan.WebTrial, SubscriptionPlan.SalesTrial].includes(subscription.value?.planCode)
  );

  const isFutureSubscription = computed(
    () => subscription.value?.status === SubscriptionStatus.Future
  );

  const isInactiveSubscription = computed(
    () =>
      isLoaded.value &&
      subscription.value?.status &&
      ![
        SubscriptionStatus.Active,
        SubscriptionStatus.InTrial,
        SubscriptionStatus.NonRenewing,
        SubscriptionStatus.Future,
      ].includes(subscription.value?.status)
  );

  const dummyDependancy = ref(0);
  setInterval(() => (dummyDependancy.value += 1), 1000 * 60);
  const daysLeft = computed(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    dummyDependancy.value;
    const daysDiff = Math.ceil(
      DateTime.fromMillis(subscription.value?.expirationDate || 0)
        .diffNow()
        .as('days')
    );
    return Math.max(0, daysDiff);
  });

  const purchasableSeatTypes: ComputedRef<SeatType[]> = computed(() => {
    if (subscription.value?.status === SubscriptionStatus.InTrial) {
      return [];
    }

    // New customers can only purchase full suite seats (one product)
    if (subscription.value?.features.some(feature => feature.id === Feature.OneProduct)) {
      return [SeatType.Suite];
    }

    const allowedToPurchase = [];
    if (subscription.value?.features.some(feature => feature.id === Feature.ContractroomPurchase)) {
      allowedToPurchase.push(SeatType.ContractRoom);
    }

    if (subscription.value?.features.some(feature => feature.id === Feature.DealroomPurchase)) {
      allowedToPurchase.push(SeatType.DealRoom);
    }

    if (allowedToPurchase.length >= 2) {
      allowedToPurchase.push(SeatType.Suite);
    }

    return allowedToPurchase;
  });

  const allowedSeatTypes: ComputedRef<SeatType[]> = computed(() => {
    const assignedSeatTypes: SeatType[] = [];

    if (totalContractSeats.value > 0) {
      assignedSeatTypes.push(SeatType.ContractRoom);
    }

    if (totalDealroomSeats.value > 0) {
      assignedSeatTypes.push(SeatType.DealRoom);
    }

    if (totalSuiteSeats.value > 0) {
      assignedSeatTypes.push(SeatType.Suite);
    }

    return [...new Set([...purchasableSeatTypes.value, ...assignedSeatTypes])];
  });

  const clamp = num => MathHelper.clamp(num, 0, Infinity);

  const totalSeats = computed(
    () => totalSuiteSeats.value + totalDealroomSeats.value + totalContractSeats.value
  );
  const activeSeats = computed(
    () => activeSuiteSeats.value + activeDealroomSeats.value + activeContractSeats.value
  );
  const hasVacantSeats = computed(
    () =>
      remainingContractSeats.value > 0 ||
      remainingDealroomSeats.value > 0 ||
      remainingSuiteSeats.value > 0
  );

  const totalSuiteSeats = computed(() => subscription.value?.seatQuantity.suite || 0);
  const activeSuiteSeats = computed(() => subscription.value?.seatCount.suiteAccess || 0);
  const remainingSuiteSeats = computed(() => clamp(totalSuiteSeats.value - activeSuiteSeats.value));

  const totalDealroomSeats = computed(() => subscription.value?.seatQuantity.dealRoom || 0);
  const activeDealroomSeats = computed(() => subscription.value?.seatCount.dealroomAccess || 0);

  const remainingDealroomSeats = computed(() =>
    clamp(totalDealroomSeats.value - activeDealroomSeats.value)
  );

  const totalContractSeats = computed(() => subscription.value?.seatQuantity.contract || 0);
  const activeContractSeats = computed(() => subscription.value?.seatCount.contractroomAccess || 0);
  const remainingContractSeats = computed(() =>
    clamp(totalContractSeats.value - activeContractSeats.value)
  );

  const getTotalSeatCount = (seatType: SeatType) => {
    switch (seatType) {
      case SeatType.Suite:
        return totalSuiteSeats.value;
      case SeatType.DealRoom:
        return totalDealroomSeats.value;
      case SeatType.ContractRoom:
        return totalContractSeats.value;
    }
  };

  const getRemainingSeatCount = (seatType: SeatType) => {
    switch (seatType) {
      case SeatType.Suite:
        return remainingSuiteSeats.value;
      case SeatType.DealRoom:
        return remainingDealroomSeats.value;
      case SeatType.ContractRoom:
        return remainingContractSeats.value;
    }
  };

  watch(
    subscription,
    (newSubscription, oldSubscription) => {
      accessChanged.value = SubscriptionHelper.hasAccessChanged(oldSubscription, newSubscription);
    },
    { deep: true }
  );

  return {
    subscription,
    isTrialPlan,
    isFutureSubscription,
    isInactiveSubscription,
    daysLeft,
    accessChanged,
    setSubscription,
    setAccessChanged,
    setIsLoaded,
    setIsError,
    isError,
    isLoaded,
    totalSeats,
    activeSeats,
    totalSuiteSeats,
    activeSuiteSeats,
    remainingSuiteSeats,
    totalDealroomSeats,
    activeDealroomSeats,
    remainingDealroomSeats,
    totalContractSeats,
    activeContractSeats,
    remainingContractSeats,
    hasVacantSeats,
    purchasableSeatTypes,
    getTotalSeatCount,
    getRemainingSeatCount,
    allowedSeatTypes,
  };
});
